import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  ScrollText, 
  CheckCircle, 
  XCircle, 
  RotateCcw, 
  Settings, 
  Plus, 
  Trash2, 
  ArrowRightLeft,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminAuditLog = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    try {
      const url = filter === 'all'
        ? `${API_URL}/api/admin/audit-logs?limit=100`
        : `${API_URL}/api/admin/audit-logs?limit=100&entity_type=${filter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLogs(data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'confirm': return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'reject': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'refund': return <RotateCcw className="w-4 h-4 text-amber-600" />;
      case 'update_settings': return <Settings className="w-4 h-4 text-blue-600" />;
      case 'create': return <Plus className="w-4 h-4 text-emerald-600" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'status_change': return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
      default: return <ScrollText className="w-4 h-4 text-slate-500" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      confirm: 'Bestätigt',
      reject: 'Abgelehnt',
      refund: 'Erstattet',
      update_settings: 'Einstellungen aktualisiert',
      create: 'Erstellt',
      delete: 'Gelöscht',
      status_change: 'Status geändert',
    };
    return labels[action] || action;
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      confirm: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      reject: 'bg-red-50 text-red-700 border-red-200',
      refund: 'bg-amber-50 text-amber-700 border-amber-200',
      update_settings: 'bg-blue-50 text-blue-700 border-blue-200',
      create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      delete: 'bg-red-50 text-red-700 border-red-200',
      status_change: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return colors[action] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getEntityLabel = (type) => {
    const labels = { booking: 'Buchung', trailer: 'Anhänger', location: 'Standort', settings: 'Einstellungen' };
    return labels[type] || type;
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    return Object.entries(details)
      .filter(([key]) => !['updated_at'].includes(key))
      .map(([key, value]) => {
        const labelMap = {
          name: 'Name', customer: 'Kunde', location: 'Standort', 
          new_status: 'Neuer Status', amount: 'Betrag', refund_id: 'Erstattungs-ID',
          booking_confirmation_mode: 'Bestätigungsmodus', company_name: 'Firmenname',
          smtp_email: 'SMTP E-Mail', google_maps_api_key: 'Maps API Key',
          customer_type: 'Kundentyp', approved: 'Genehmigt', changes: 'Änderungen',
          invoice_number: 'Rechnungsnr.', customer_email: 'E-Mail',
        };
        const label = labelMap[key] || key;
        const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return `${label}: ${displayValue}`;
      })
      .join(' | ');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-audit-log-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">Audit-Log</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Alle Admin-Aktionen werden hier protokolliert
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="audit-log-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="booking">Buchungen</SelectItem>
              <SelectItem value="trailer">Anhänger</SelectItem>
              <SelectItem value="location">Standorte</SelectItem>
              <SelectItem value="settings">Einstellungen</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {logs.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <ScrollText className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Einträge</h3>
            <p className="text-[hsl(var(--muted-foreground))]">
              Es wurden noch keine Admin-Aktionen protokolliert.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-shadow">
          <CardContent className="p-0">
            <div className="divide-y divide-[hsl(var(--border))]">
              {logs.map((log, index) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                  className="flex items-start gap-4 p-4 hover:bg-[hsl(var(--muted)/0.3)] transition-colors"
                >
                  <div className="mt-0.5">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge variant="outline" className={getActionBadgeColor(log.action)}>
                        {getActionLabel(log.action)}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                        {getEntityLabel(log.entity_type)}
                      </Badge>
                      {log.entity_id && log.entity_id !== 'app_settings' && (
                        <span className="text-xs text-[hsl(var(--muted-foreground))] font-mono">
                          {log.entity_id.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                    {formatDetails(log.details) && (
                      <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                        {formatDetails(log.details)}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        von {typeof log.admin_name === 'string' ? log.admin_name : (log.admin_name?.name || log.admin_name?.email || 'Admin')}
                      </span>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: de })}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAuditLog;
